/**
 * Client-side homeowner profile API module — replaces homeowner-profile.action.ts.
 */

import { HOME_OWNERS, HOME } from "@/shared/constants/backend-api"
import { authFetchJson, getUserIdFromToken } from "@/shared/lib/auth-fetch"
import { ApiResponse } from "@/shared/types/backend-response.type"
import { HomeownerProfileFormType } from "../schemas/homeowner-profile-form.schema"
import { getHomeOwner } from "@/features/homeowners/api/homeowners.api"
import { transformToE164 } from "@/shared/lib/utils/phone.utils"

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const HOMEOWNER_PROFILE_QUERY_KEYS = {
  all: ["homeowner-profile"] as const,
  detail: (userId: number) => ["homeowner-profile", userId] as const,
}

// ---------------------------------------------------------------------------
// Read
// ---------------------------------------------------------------------------

export async function getHomeownerProfile(userId?: number): Promise<ApiResponse<any>> {
  const uid = userId !== undefined ? userId : getUserIdFromToken()
  return authFetchJson<any>(HOME_OWNERS.byUserId(uid))
}

// ---------------------------------------------------------------------------
// Update
// ---------------------------------------------------------------------------

export async function updateHomeownerProfile(
  data: Partial<HomeownerProfileFormType>,
  homeownerId?: number,
  userId?: number,
): Promise<ApiResponse<any>> {
  try {
    let homeId: number | undefined
    const finalUserId = userId !== undefined ? userId : getUserIdFromToken()

    if (!homeownerId) {
      if (!finalUserId) return { success: false, status: 400, message: "User ID required" } as ApiResponse<any>
      const hoRes = await getHomeownerProfile(finalUserId)
      if (!hoRes.success || !hoRes.data) {
        return { success: false, status: 404, message: "Homeowner profile not found" } as ApiResponse<any>
      }
      homeownerId = hoRes.data.id
      homeId = hoRes.data.home?.id
    } else {
      const hoRes = await getHomeOwner(homeownerId)
      if (!hoRes.success || !hoRes.data) {
        return { success: false, status: 404, message: "Homeowner profile not found" } as ApiResponse<any>
      }
      homeId = hoRes.data.home?.id
    }

    if (!homeownerId) {
      return { success: false, status: 404, message: "Homeowner ID not found" } as ApiResponse<any>
    }

    // Update homeowner data
    const homeownerData = data.homeOwner
    let homeownerUpdateRes: ApiResponse<any> | null = null

    if (homeownerData) {
      const updateBody: any = {}

      if (homeownerData.name !== undefined) updateBody.name = homeownerData.name
      if (homeownerData.email !== undefined) updateBody.email = homeownerData.email
      if (homeownerData.primaryLanguage !== undefined) updateBody.primaryLanguage = homeownerData.primaryLanguage
      if (homeownerData.secondaryContact !== undefined) updateBody.secondaryContact = homeownerData.secondaryContact
      if (homeownerData.dateOfBirth !== undefined) {
        if (homeownerData.dateOfBirth instanceof Date) {
          const y = homeownerData.dateOfBirth.getFullYear()
          const m = String(homeownerData.dateOfBirth.getMonth() + 1).padStart(2, "0")
          const d = String(homeownerData.dateOfBirth.getDate()).padStart(2, "0")
          updateBody.dateOfBirth = `${y}-${m}-${d}`
        } else {
          updateBody.dateOfBirth = homeownerData.dateOfBirth
        }
      }
      if (homeownerData.residentType !== undefined) updateBody.residentType = homeownerData.residentType
      if (homeownerData.medicalNeeds !== undefined) updateBody.medicalNeeds = homeownerData.medicalNeeds
      if (homeownerData.numChildrenUnder18 !== undefined) updateBody.numChildrenUnder18 = homeownerData.numChildrenUnder18
      if (homeownerData.isFirstResponder !== undefined) updateBody.isFirstResponder = homeownerData.isFirstResponder
      if (homeownerData.isSingleParent !== undefined) updateBody.isSingleParent = homeownerData.isSingleParent
      if (homeownerData.isVeteran !== undefined) updateBody.isVeteran = homeownerData.isVeteran
      if (homeownerData.appliedFEMA !== undefined) updateBody.appliedFEMA = homeownerData.appliedFEMA
      if (homeownerData.femaStatus !== undefined) updateBody.femaStatus = homeownerData.femaStatus
      if (homeownerData.appliedSBA !== undefined) updateBody.appliedSBA = homeownerData.appliedSBA
      if (homeownerData.sbaStatus !== undefined) updateBody.sbaStatus = homeownerData.sbaStatus
      if (homeownerData.safetyConcerns !== undefined) updateBody.safetyConcerns = homeownerData.safetyConcerns
      if (homeownerData.notesAndComments !== undefined) updateBody.notesAndComments = homeownerData.notesAndComments
      if (homeownerData.ownerStory !== undefined) updateBody.ownerStory = homeownerData.ownerStory

      // Phone transformation
      if (homeownerData.phone !== undefined && homeownerData.phone?.trim()) {
        const transformed = transformToE164(homeownerData.phone)
        if (transformed?.trim()) updateBody.phone = transformed
      }
      if (homeownerData.secondaryPhone !== undefined && homeownerData.secondaryPhone?.trim()) {
        const transformed = transformToE164(homeownerData.secondaryPhone)
        if (transformed?.trim()) updateBody.secondaryPhone = transformed
      }

      homeownerUpdateRes = await authFetchJson<any>(HOME_OWNERS.byId(homeownerId), {
        method: "PUT",
        body: JSON.stringify(updateBody),
      })
    }

    // Update home data
    let homeUpdateRes: ApiResponse<any> | null = null
    if (homeId && data.home) {
      const homeUpdateBody: any = {}
      const homeData = data.home

      Object.keys(homeData).forEach((key) => {
        if (!["repairabilityInfo", "googlePicLink", "materialsNeeded", "id", "regionId"].includes(key)) {
          homeUpdateBody[key] = homeData[key as keyof typeof homeData]
        }
      })

      if (homeData.repairabilityInfo) {
        const repairabilityInfo: any = {}
        Object.keys(homeData.repairabilityInfo).forEach((key) => {
          if (key !== "ownerPhoneNumberIfRental") {
            repairabilityInfo[key] = (homeData.repairabilityInfo as any)[key]
          }
        })
        const phoneValue = (homeData.repairabilityInfo as any)?.ownerPhoneNumberIfRental
        if (phoneValue && typeof phoneValue === "string" && phoneValue.trim()) {
          const transformed = transformToE164(phoneValue)
          if (transformed?.trim()) repairabilityInfo.ownerPhoneNumberIfRental = transformed
        }
        if (Object.keys(repairabilityInfo).length > 0) {
          homeUpdateBody.repairabilityInfo = repairabilityInfo
        }
      }

      homeUpdateRes = await authFetchJson<any>(`${HOME.base}/${homeId}`, {
        method: "PATCH",
        body: JSON.stringify(homeUpdateBody),
      })
    }

    const homeownerSuccess = homeownerData ? (homeownerUpdateRes?.success ?? false) : true
    const homeSuccess = homeId && data.home ? (homeUpdateRes?.success ?? false) : true
    const overallSuccess = homeownerSuccess && homeSuccess

    let errorMessage = "Profile updated successfully"
    if (!overallSuccess) {
      if (!homeownerSuccess && homeownerUpdateRes && !homeownerUpdateRes.success) {
        errorMessage = homeownerUpdateRes.message || "Failed to update homeowner information"
      } else if (!homeSuccess && homeUpdateRes && !homeUpdateRes.success) {
        errorMessage = homeUpdateRes.message || "Failed to update home information"
      } else {
        errorMessage = "Failed to update profile"
      }
    }

    return {
      success: overallSuccess,
      status: overallSuccess ? 200 : 400,
      message: errorMessage,
      data: {
        homeowner: homeownerUpdateRes?.success ? homeownerUpdateRes.data : undefined,
        home: homeUpdateRes?.success ? homeUpdateRes.data : undefined,
      },
    } as ApiResponse<any>
  } catch (error) {
    return {
      success: false,
      status: 500,
      message: error instanceof Error ? error.message : "An error occurred",
    } as ApiResponse<any>
  }
}
